import type { Ref, SelectHTMLAttributes } from 'react'
import { CaretDown } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { controlBase, controlBorder } from './controlStyles'
import { useFieldControl } from './fieldContext'

export interface SelectOption<V extends string = string> {
  value: V
  label: string
  disabled?: boolean
}

export interface SelectProps<V extends string = string> extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'value' | 'size'
> {
  options: ReadonlyArray<SelectOption<V>>
  value: V | ''
  onChange: (value: V) => void
  placeholder?: string
  size?: 'md' | 'touch'
  invalid?: boolean
  ref?: Ref<HTMLSelectElement>
}

/** Native select — best on desktop console screens. Phones use PickerSheet for big targets. */
export function Select<V extends string = string>({
  options,
  value,
  onChange,
  placeholder,
  size = 'md',
  invalid,
  className,
  id,
  ref,
  ...rest
}: SelectProps<V>) {
  const field = useFieldControl()
  const isInvalid = invalid ?? field?.invalid ?? false
  return (
    <div className="relative">
      <select
        ref={ref}
        id={id ?? field?.id}
        aria-describedby={field?.describedBy}
        aria-invalid={isInvalid || undefined}
        value={value}
        onChange={(e) => onChange(e.target.value as V)}
        className={cn(
          controlBase,
          controlBorder(isInvalid),
          'appearance-none pr-9',
          size === 'touch' ? 'h-12 px-3.5 text-[17px]' : 'h-10 px-3 text-[15px]',
          value === '' && 'text-muted',
          className,
        )}
        {...rest}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <CaretDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
      />
    </div>
  )
}
